/**
 * assets/otp-widget.js
 * -----------------------------------------------------------------
 * Load this ONCE, globally, e.g. in theme.liquid before </body>:
 *   <script src="{{ 'otp-widget.js' | asset_url }}" defer></script>
 *
 * Do NOT include it inside cart-drawer.liquid itself — that file
 * gets re-rendered/re-injected on every AJAX cart refresh in most
 * themes (including Vogal), which is exactly what causes duplicate
 * widgets and duplicate event listeners. Loading the script once at
 * the theme level and re-binding to the DOM node each time the
 * drawer re-renders is the fix.
 *
 * Architecture:
 *  - Single global namespace `window.__otpWidget`, guarded so this
 *    file can safely execute more than once (e.g. Shopify sections
 *    reloading assets) without double-initializing.
 *  - A MutationObserver watches for the cart drawer's OTP container
 *    appearing/reappearing in the DOM (covers open/close, quantity
 *    change, add/remove item — anything that re-renders the drawer)
 *    and re-binds exactly once per DOM node via a WeakSet guard.
 *  - No setInterval polling loop anywhere. The resend cooldown uses
 *    a single setTimeout scheduled against a stored epoch timestamp,
 *    recomputed on rebind so it survives drawer re-renders.
 *  - State (phone, verified flag, token, payment method, cooldown
 *    expiry) lives in sessionStorage so it survives drawer re-renders
 *    and even a full page reload within the same tab session, per
 *    your requirement.
 *  - Checkout button is disabled purely via a data attribute +
 *    CSS/pointer-events, re-evaluated on every state change and on
 *    every rebind, rather than trusting whatever DOM state existed
 *    before a redraw.
 */

(function () {
  "use strict";

  const STORAGE_KEY = "otpWidgetState:v1";
  const CONTAINER_SELECTOR = ".otp-widget"; // class-based: supports multiple instances (drawer + cart page)
  const DEFAULT_CHECKOUT_SELECTOR = "#CartDrawer-Checkout"; // fallback if a container doesn't specify its own

  function getCheckoutSelector(container) {
    return container.getAttribute("data-checkout-selector") || DEFAULT_CHECKOUT_SELECTOR;
  }

  // Guard against this script executing twice (theme re-injects assets, etc.)
  if (window.__otpWidgetLoaded) return;
  window.__otpWidgetLoaded = true;

  console.log("[otp-widget] script loaded and running");

  // WeakSet tracks which container DOM nodes already have listeners bound,
  // so re-running bind() on the same still-mounted node is a no-op, and a
  // brand-new node (post drawer re-render) gets bound exactly once.
  const boundNodes = new WeakSet();

  function loadState() {
    try {
      const raw = sessionStorage.getItem(STORAGE_KEY);
      if (!raw) return defaultState();
      const parsed = JSON.parse(raw);
      return { ...defaultState(), ...parsed };
    } catch (e) {
      return defaultState();
    }
  }

  function defaultState() {
    return {
      paymentMethod: "prepaid",
      phone: "",
      verifiedPhone: "",
      verified: false,
      token: "",
      channel: "sms",
      requestId: "",
      cooldownUntil: 0, // epoch ms
    };
  }

  function saveState(state) {
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    } catch (e) {
      /* sessionStorage unavailable (private mode etc.) — degrade silently,
         widget still works for the current DOM lifetime */
    }
  }

  let state = loadState();

  function setState(patch) {
    state = { ...state, ...patch };
    saveState(state);
  }

  function normalizeDigits(v) {
    return (v || "").replace(/\D/g, "");
  }

  function isValidMobile(v) {
    const d = normalizeDigits(v);
    return /^[6-9]\d{9}$/.test(d);
  }

  function setCheckoutEnabled(container, enabled) {
    const selector = getCheckoutSelector(container);
    const btns = document.querySelectorAll(selector);
    console.log("[otp-widget] setCheckoutEnabled:", enabled, "| buttons matched:", btns.length, "| selector:", selector);
    btns.forEach((btn) => {
      if (enabled) {
        btn.removeAttribute("disabled");
        btn.classList.remove("is-otp-locked");
      } else {
        btn.setAttribute("disabled", "disabled");
        btn.classList.add("is-otp-locked");
      }
    });
  }

  function evaluateCheckoutGate(container) {
    // Core COD-gating rule: checkout stays disabled ONLY when COD is
    // selected and not yet verified. Prepaid always passes immediately.
    console.log("[otp-widget] evaluateCheckoutGate | paymentMethod:", state.paymentMethod, "| verified:", state.verified);
    if (state.paymentMethod === "prepaid") {
      setCheckoutEnabled(container, true);
      return;
    }
    const codVerified = state.verified && state.verifiedPhone === state.phone;
    setCheckoutEnabled(container, codVerified);
  }

  function showMessage(container, text, type) {
    const el = container.querySelector("[data-otp-message]");
    if (!el) return;
    el.textContent = text || "";
    el.className = "otp-widget__message" + (type ? " otp-widget__message--" + type : "");
  }

  function proxyUrl(container, path) {
    const base = container.getAttribute("data-otp-proxy-url");
    return base.replace(/\/$/, "") + path;
  }

  async function apiCall(container, path, payload) {
    const res = await fetch(proxyUrl(container, path), {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
    let data;
    try {
      data = await res.json();
    } catch (e) {
      throw { networkError: true };
    }
    if (!res.ok) throw { ...data, httpStatus: res.status };
    return data;
  }

  function render(container) {
    const codPanel = container.querySelector("[data-cod-panel]");
    const verifiedView = container.querySelector("[data-verified-view]");
    const form = container.querySelector("[data-otp-form]");
    const phoneInput = container.querySelector("[data-phone-input]");
    const otpRow = container.querySelector("[data-otp-input-row]");
    const resendRow = container.querySelector("[data-resend-row]");
    const verifiedNumberEl = container.querySelector("[data-verified-number]");
    const paymentRadios = container.querySelectorAll("[data-payment-radio]");

    paymentRadios.forEach((r) => {
      r.checked = r.value === state.paymentMethod;
    });

    codPanel.hidden = state.paymentMethod !== "cod";

    const isVerified = state.verified && state.verifiedPhone === state.phone && state.phone;

    verifiedView.hidden = !isVerified;
    form.hidden = !!isVerified;

    if (isVerified) {
      verifiedNumberEl.textContent = "+91 " + state.verifiedPhone;
    }

    if (phoneInput && document.activeElement !== phoneInput) {
      phoneInput.value = state.phone || "";
    }

    // Lock the phone field once an OTP has been requested (requestId set)
    // but not yet verified — customer can't silently swap numbers mid-flow.
    const otpRequested = !!state.requestId && !isVerified;
    if (phoneInput) phoneInput.disabled = otpRequested;

    otpRow.hidden = !otpRequested;
    resendRow.hidden = !otpRequested;

    container.querySelectorAll("[data-channel]").forEach((btn) => {
      btn.classList.toggle("is-active", btn.getAttribute("data-channel") === state.channel);
      // Channel stays switchable even after requesting an OTP — only the
      // phone number itself locks. This lets a customer switch to
      // WhatsApp if an SMS doesn't arrive, without resetting their number.
    });

    updateCooldownUI(container);
    evaluateCheckoutGate(container);
  }

  let cooldownTimeoutId = null;

  function updateCooldownUI(container) {
    const resendBtn = container.querySelector("[data-resend-otp]");
    const timerEl = container.querySelector("[data-resend-timer]");
    if (!resendBtn || !timerEl) return;

    if (cooldownTimeoutId) {
      clearTimeout(cooldownTimeoutId);
      cooldownTimeoutId = null;
    }

    const remaining = Math.max(0, Math.ceil((state.cooldownUntil - Date.now()) / 1000));

    if (remaining <= 0) {
      resendBtn.disabled = false;
      timerEl.textContent = "0";
      return;
    }

    resendBtn.disabled = true;
    timerEl.textContent = String(remaining);

    // Single scheduled timeout, not a repeating interval. Re-checks the
    // stored epoch each time so it self-corrects if the tab was backgrounded.
    cooldownTimeoutId = setTimeout(() => updateCooldownUI(container), 1000);
  }

  async function handleSendOtp(container) {
    const phoneInput = container.querySelector("[data-phone-input]");
    const sendBtn = container.querySelector("[data-send-otp]");
    const spinner = container.querySelector("[data-btn-spinner]");
    const label = container.querySelector("[data-btn-label]");
    const phone = normalizeDigits(phoneInput.value);

    if (!isValidMobile(phone)) {
      showMessage(container, "Enter a valid 10-digit Indian mobile number.", "error");
      return;
    }
    if (Date.now() < state.cooldownUntil) {
      showMessage(container, "Please wait before requesting another OTP.", "error");
      return;
    }

    setState({ phone });
    sendBtn.disabled = true;
    spinner.hidden = false;
    label.textContent = "Sending…";
    showMessage(container, "");

    try {
      const data = await apiCall(container, "/send", { phone, channel: state.channel });
      setState({
        requestId: data.requestId,
        cooldownUntil: Date.now() + (data.cooldown || 10) * 1000,
        verified: false,
      });
      showMessage(container, `OTP sent via ${state.channel === "sms" ? "SMS" : "WhatsApp"}.`, "success");
    } catch (err) {
      if (err.error === "COOLDOWN") {
        showMessage(container, "Please wait a few seconds before retrying.", "error");
      } else if (err.error === "RATE_LIMIT") {
        showMessage(container, "Too many attempts. Please try again later.", "error");
      } else if (err.error === "INVALID_PHONE") {
        showMessage(container, "That mobile number looks invalid.", "error");
      } else if (err.error === "PROVIDER_UNAVAILABLE") {
        showMessage(container, "SMS/WhatsApp service is temporarily unavailable. Please try again shortly.", "error");
      } else {
        showMessage(container, "Something went wrong. Please try again.", "error");
      }
    } finally {
      spinner.hidden = true;
      label.textContent = "Send OTP";
      sendBtn.disabled = false;
      render(container);
    }
  }

  async function saveVerifiedPhoneToCart(phone, channel) {
    // Save the verified phone number AND channel as cart attributes using
    // Shopify's own cart API. This travels automatically with the order,
    // so a webhook can later compare/record it against the actual order.
    try {
      await fetch("/cart/update.js", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          attributes: {
            otp_verified_phone: phone,
            otp_verified_channel: phone ? (channel || "") : "",
          },
        }),
      });
    } catch (e) {
      // Non-fatal — the customer's checkout flow shouldn't break if this
      // fails; it only affects the after-the-fact order-flagging safety net.
      console.log("[otp-widget] could not save verified phone to cart:", e);
    }
  }

  async function handleVerifyOtp(container) {
    const otpInput = container.querySelector("[data-otp-input]");
    const verifyBtn = container.querySelector("[data-verify-otp]");
    const spinner = container.querySelector("[data-verify-spinner]");
    const label = container.querySelector("[data-verify-label]");
    const otp = normalizeDigits(otpInput.value);

    if (otp.length !== 6) {
      showMessage(container, "Enter the 6-digit OTP.", "error");
      return;
    }

    verifyBtn.disabled = true;
    spinner.hidden = false;
    label.textContent = "Verifying…";
    showMessage(container, "");

    try {
      const data = await apiCall(container, "/verify", {
        phone: state.phone,
        otp,
        requestId: state.requestId,
      });
      setState({
        verified: true,
        verifiedPhone: state.phone,
        token: data.token,
      });
      showMessage(container, "Number verified successfully.", "success");
      saveVerifiedPhoneToCart(state.phone, state.channel);
    } catch (err) {
      if (err.error === "WRONG_OTP") {
        showMessage(container, "Incorrect OTP. Please try again.", "error");
      } else if (err.error === "TOO_MANY_ATTEMPTS") {
        showMessage(container, "Too many incorrect attempts. Please request a new OTP.", "error");
        setState({ requestId: "" });
      } else if (err.error === "EXPIRED_OR_INVALID") {
        showMessage(container, "This OTP has expired. Please request a new one.", "error");
        setState({ requestId: "" });
      } else {
        showMessage(container, "Verification failed. Please try again.", "error");
      }
    } finally {
      spinner.hidden = true;
      label.textContent = "Verify";
      verifyBtn.disabled = false;
      render(container);
    }
  }

  function handleChangeNumber(container) {
    setState({
      phone: "",
      verified: false,
      verifiedPhone: "",
      token: "",
      requestId: "",
      cooldownUntil: 0,
    });
    const phoneInput = container.querySelector("[data-phone-input]");
    const otpInput = container.querySelector("[data-otp-input]");
    if (phoneInput) phoneInput.value = "";
    if (otpInput) otpInput.value = "";
    showMessage(container, "");
    saveVerifiedPhoneToCart("", ""); // clear the stale attributes on the cart too
    render(container);
  }

  function bind(container) {
    if (boundNodes.has(container)) return; // already bound to this exact node
    boundNodes.add(container);
    console.log("[otp-widget] binding to container", container);

    const radios = container.querySelectorAll("[data-payment-radio]");
    console.log("[otp-widget] payment radios found:", radios.length);
    radios.forEach((radio) => {
      radio.addEventListener("change", (e) => {
        console.log("[otp-widget] payment method changed to:", e.target.value);
        setState({ paymentMethod: e.target.value });
        render(container);
      });
    });

    container.querySelectorAll("[data-channel]").forEach((btn) => {
      btn.addEventListener("click", () => {
        if (btn.disabled) return;
        setState({ channel: btn.getAttribute("data-channel") });
        render(container);
      });
    });

    const phoneInput = container.querySelector("[data-phone-input]");
    if (phoneInput) {
      phoneInput.addEventListener("input", (e) => {
        // Phone field is disabled once requestId is set, so this only
        // fires pre-OTP-request — satisfies "reset only if number changes"
        // while the lock above satisfies "cannot switch after requesting".
        const digits = normalizeDigits(e.target.value).slice(0, 10);
        e.target.value = digits;
        if (digits !== state.phone) {
          setState({ verified: false, requestId: "" });
        }
        setState({ phone: digits });
      });
    }

    const sendBtn = container.querySelector("[data-send-otp]");
    if (sendBtn) sendBtn.addEventListener("click", () => handleSendOtp(container));

    const resendBtn = container.querySelector("[data-resend-otp]");
    if (resendBtn) resendBtn.addEventListener("click", () => handleSendOtp(container));

    const verifyBtn = container.querySelector("[data-verify-otp]");
    if (verifyBtn) verifyBtn.addEventListener("click", () => handleVerifyOtp(container));

    const changeBtn = container.querySelector("[data-change-number]");
    if (changeBtn) changeBtn.addEventListener("click", () => handleChangeNumber(container));

    render(container);
  }

  function tryBindExisting() {
    const containers = document.querySelectorAll(CONTAINER_SELECTOR);
    if (containers.length === 0) {
      console.log("[otp-widget] container not found:", CONTAINER_SELECTOR);
      return;
    }
    containers.forEach((container) => bind(container));
  }

  // Covers: drawer open/close, add item, remove item, quantity change —
  // any of these that re-render the drawer markup will trigger this
  // observer, and boundNodes ensures we only attach listeners once per
  // actual DOM node (a fresh re-render creates a new node -> gets bound;
  // an untouched node is skipped).
  const observer = new MutationObserver(() => {
    tryBindExisting();
  });
  observer.observe(document.documentElement, { childList: true, subtree: true });

  document.addEventListener("DOMContentLoaded", tryBindExisting);
  tryBindExisting();

  // Re-evaluate the checkout gate whenever Shopify's own cart events fire,
  // in case the theme swaps the checkout button element itself.
  document.addEventListener("cart:updated", () => {
    document.querySelectorAll(CONTAINER_SELECTOR).forEach((container) => evaluateCheckoutGate(container));
  });
})();
